export const teamColors = [
  '#66c5cc',
  '#f6cf71',
  '#f89c74',
  '#dcb0f2',
  '#87c55f',
  '#9eb9f3',
  '#fe88b1',
  '#c9db74',
  '#8be0a4',
  '#b497e7',
  '#d3b484',
  '#b3b3b3',
];

interface HSL {
  h: number;
  s: number;
  l: number;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) throw new Error(`Invalid hex color: ${hex}`);

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

function rgbToHsl(r: number, g: number, b: number): HSL {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;
  const l = (max + min) / 2;

  let s = 0;
  if (diff !== 0) {
    s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min);
  }

  let h = 0;
  if (diff !== 0) {
    switch (max) {
      case r:
        h = (g - b) / diff + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / diff + 2;
        break;
      case b:
        h = (r - g) / diff + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToRgb(
  h: number,
  s: number,
  l: number
): { r: number; g: number; b: number } {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number): string => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function hexToHsl(hex: string): HSL {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHsl(r, g, b);
}

function hslToHex(hsl: HSL): string {
  const { r, g, b } = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(r, g, b);
}

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function getTeamColors(teamCount: number, random = false): string[] {
  if (teamCount < teamColors.length && random) {
    return shuffleArray(teamColors).slice(0, teamCount);
  }

  if (teamCount <= teamColors.length) {
    return teamColors.slice(0, teamCount);
  }

  // Analyze existing colors
  const hslColors = teamColors.map(hexToHsl);
  const hues = hslColors.map(c => c.h).sort((a, b) => a - b);
  const saturations = hslColors.map(c => c.s);
  const lightnesses = hslColors.map(c => c.l);

  const avgSat = Math.round(
    saturations.reduce((sum, s) => sum + s, 0) / saturations.length
  );
  const avgLight = Math.round(
    lightnesses.reduce((sum, l) => sum + l, 0) / lightnesses.length
  );

  // Find hue gaps
  const hueGaps: Array<{ start: number; size: number }> = [];
  for (let i = 0; i < hues.length; i++) {
    const currentHue = hues[i];
    const nextHue = i === hues.length - 1 ? hues[0] + 360 : hues[i + 1];
    const gap = nextHue - currentHue;

    if (gap > 20) {
      hueGaps.push({
        start: currentHue,
        size: gap > 360 ? gap - 360 : gap,
      });
    }
  }
  hueGaps.sort((a, b) => b.size - a.size);

  const colors = [...teamColors];
  const needed = teamCount - teamColors.length;
  const satVariations = [0, 5, -5, 8, -8, 3, -3, 6, -6, 4, -4, 7, -7];
  const lightVariations = [0, 3, -3, 5, -5, 2, -2, 4, -4, 6, -6, 1, -1];

  // Fill hue gaps first
  for (let i = 0; i < Math.min(needed, hueGaps.length); i++) {
    const gap = hueGaps[i];
    const newHue = Math.round((gap.start + gap.size / 2) % 360);
    const satVar = satVariations[i % satVariations.length];
    const lightVar = lightVariations[i % lightVariations.length];

    const color = hslToHex({
      h: newHue,
      s: Math.max(40, Math.min(85, avgSat + satVar)),
      l: Math.max(55, Math.min(85, avgLight + lightVar)),
    });

    colors.push(color);
  }

  // Use golden ratio for remaining colors
  const remaining = teamCount - colors.length;
  if (remaining > 0) {
    const goldenRatio = 137.508;
    let currentHue = Math.max(...hues);

    for (let i = 0; i < remaining; i++) {
      currentHue = (currentHue + goldenRatio) % 360;
      const satVar = satVariations[(i + hueGaps.length) % satVariations.length];
      const lightVar =
        lightVariations[(i + hueGaps.length) % lightVariations.length];

      const color = hslToHex({
        h: Math.round(currentHue),
        s: Math.max(45, Math.min(80, avgSat + satVar)),
        l: Math.max(58, Math.min(82, avgLight + lightVar)),
      });

      colors.push(color);
    }
  }

  return colors;
}
